import { useFieldArray, useForm } from "react-hook-form";

export function useFixatServiceForm(serviceToEdit) {
  const {
    register,
    control,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({
    values: serviceToEdit || defaultFormValues,
    mode: "onTouched",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "tab_discount",
  });

  return {
    register,
    control,
    reset,
    getValues,
    handleSubmit,
    errors,
    tabDiscount: fields,
    addDiscount: () => append(defaultTabDiscountValues),
    removeDiscount: remove,
  };
}

const defaultFormValues = {
  sku: "",
  name: "",
  price: "",
  currency: "",
  legal_type: "",
  periodicity: "",
  category: "",
  time_periods: "",
  description: "",
  status: "active",
  priority: 1,
  minimum_periods: "",
  tab_discount: [],
  terms_and_conditions: "",
  cancelation_clauses: "",
  service_reach: "",
};

const defaultTabDiscountValues = {
  min_quantity: "",
  max_quantity: "",
  max_discount: "",
};

const fixatServiceValidationRules = {
  sku: {
    required: "*El SKU es obligatorio",
  },
  name: {
    required: "*El nombre es obligatorio",
  },
  short_name: {
    required: "*El nombre corto es obligatorio",
  },
  price: {
    required: "*El precio es obligatorio",
    valueAsNumber: true,
    validate: (v) => v > 0 || "*El precio debe ser mayor a 0",
  },
  currency: {
    required: "*La moneda es obligatoria",
  },
  legal_type: {
    required: "*El tipo de persona es obligatorio",
  },
  periodicity: {
    required: "*La periodicidad es obligatoria",
  },
  category: {
    required: "*La categoría es obligatoria",
  },
  time_periods: {
    required: "*El periodo de tiempo es obligatorio",
  },
  description: {
    required: "*La descripción es obligatoria",
  },
  status: {
    required: "*El estatus es obligatorio",
  },
  priority: {
    required: "*La prioridad es obligatoria",
    valueAsNumber: true,
    validate: (v) => v > 0 || "Debe ser mayor a 0",
  },
  minimum_periods: {
    required: "*El número mínimo de periodos es obligatorio",
    valueAsNumber: true,
    validate: (v) => v > 0 || "*Debe ser mayor a 0",
  },
  terms_and_conditions: {
    required: "*Los términos y condiciones son obligatorios",
    validate: (v) =>
      (v && v.replace(/<[^>]+>/g, "").trim().length > 0) ||
      "*Este campo no puede estar vacío",
  },
  cancelation_clauses: {
    required: "*Las cláusulas de cancelación son obligatorias",
    validate: (v) =>
      (v && v.replace(/<[^>]+>/g, "").trim().length > 0) ||
      "*Este campo no puede estar vacío",
  },
  service_reach: {
    required: "*El alcance del servicio es obligatorio",
    validate: (v) =>
      (v && v.replace(/<[^>]+>/g, "").trim().length > 0) ||
      "*Este campo no puede estar vacío",
  },
};

export const tabDiscountValidationRules = (name, index, getValues) => {
  switch (name) {
    case "max_discount":
      return {
        required: "*El descuento máximo es obligatorio",
        valueAsNumber: true,
        validate: (v) => v >= 0 || "*Debe ser mayor o igual a 0",
      };
    case "max_quantity":
      return {
        required: "*Cantidad máxima es obligatoria",
        valueAsNumber: true,
        validate: (v) => {
          const min = getValues(`tab_discount.${index}.min_quantity`);
          if (v <= 0) return "*Debe ser mayor a 0";
          if (min && v <= min) return "*Debe ser mayor que la cantidad mínima";
          return true;
        },
      };
    case "min_quantity":
      return {
        required: "*Cantidad mínima es obligatoria",
        valueAsNumber: true,
        validate: (v) => {
          const maxQuantityInUse = Math.max(
            ...getValues("tab_discount")
              .slice(0, index)
              .filter((discount) => !isNaN(discount.max_quantity))
              .map((discount) => discount.max_quantity)
          );
          const max = getValues(`tab_discount.${index}.max_quantity`);
          if (v <= 0) return "*Debe ser mayor a 0";
          if (max && v >= max) return "*Debe ser menor que la cantidad máxima";
          if (maxQuantityInUse && v <= maxQuantityInUse)
            return "*Cantidad en uso";
          return true;
        },
      };
    default:
      throw new Error("Tab discount field not allowed");
  }
};

export const tabDiscountFields = [
  {
    name: "min_quantity",
    label: "Cantidad mínima",
    variant: "input",
    inputType: "number",
    placeholder: "Ingresa la cantidad mínima",
  },
  {
    name: "max_quantity",
    label: "Cantidad máxima",
    variant: "input",
    inputType: "number",
    placeholder: "Ingresa la cantidad máxima",
  },
  {
    name: "max_discount",
    label: "Descuento máximo",
    variant: "input",
    inputType: "number",
    placeholder: "Descuento",
  },
];

export const textEditorFields = [
  {
    name: "service_reach",
    label: "Alcance del servicio",
    placeholder: "Ingresa el alcance del servicio...",
    rules: fixatServiceValidationRules.service_reach,
  },
  {
    name: "terms_and_conditions",
    label: "Términos y condiciones",
    placeholder: "Ingresa los términos y condiciones del servicio...",
    rules: fixatServiceValidationRules.terms_and_conditions,
  },
  {
    name: "cancelation_clauses",
    label: "Cláusulas de cancelación",
    placeholder: "Ingresa las cláusulas de cancelación del servicio...",
    rules: fixatServiceValidationRules.cancelation_clauses,
  },
];

export const fixatServiceFormFields = [
  {
    name: "name",
    label: "Nombre",
    variant: "input",
    inputType: "text",
    placeholder: "Ingresa el nombre del servicio",
    rules: fixatServiceValidationRules.name,
    className: "col-span-full",
  },
  {
    name: "short_name",
    label: "Nombre corto",
    variant: "input",
    inputType: "text",
    placeholder: "Ingresa el nombre corto del servicio",
    rules: fixatServiceValidationRules.short_name,
  },
  {
    name: "sku",
    label: "SKU",
    variant: "input",
    inputType: "text",
    placeholder: "Ingresa el SKU del servicio",
    rules: fixatServiceValidationRules.sku,
  },
  {
    name: "description",
    label: "Descripción",
    variant: "textarea",
    placeholder: "Ingresa la descripción del servicio",
    rules: fixatServiceValidationRules.description,
    className: "col-span-full",
  },
  {
    name: "price",
    label: "Precio",
    variant: "input",
    inputType: "number",
    placeholder: "Ingresa el precio del servicio",
    rules: fixatServiceValidationRules.price,
  },
  {
    name: "currency",
    label: "Moneda",
    variant: "select",
    options: [{ value: "MXN", label: "MXN" }],
    rules: fixatServiceValidationRules.currency,
  },
  {
    name: "category",
    label: "Categoría",
    variant: "select",
    options: [
      { value: "regularization", label: "Regularización" },
      { value: "accounting", label: "Servicios Contables" },
    ],
    rules: fixatServiceValidationRules.category,
  },
  {
    name: "legal_type",
    label: "Tipo de persona",
    variant: "select",
    options: [
      { value: "personal", label: "Persona Física" },
      { value: "enterprise", label: "Persona Moral" },
    ],
    rules: fixatServiceValidationRules.legal_type,
  },
  {
    name: "periodicity",
    label: "Periodicidad",
    variant: "select",
    options: [
      { value: "monthly", label: "Mensual" },
      { value: "quarterly", label: "Trimestral" },
      { value: "bimonthly", label: "Bimestral" },
      { value: "unique", label: "Único" },
      { value: "yearly", label: "Anual" },
    ],
    rules: fixatServiceValidationRules.periodicity,
  },
  // {
  //   name: "status",
  //   label: "Estatus",
  //   variant: "select",
  //   options: [
  //     { value: "active", label: "Activo" },
  //     { value: "inactive", label: "Desactivado" },
  //   ],
  //   rules: fixatServiceValidationRules.status,
  // },
  {
    name: "time_periods",
    label: "Periodo de tiempo",
    variant: "select",
    options: [
      { value: "lt", label: "Menor" },
      { value: "lte", label: "Menor o igual" },
      { value: "gt", label: "Mayor" },
      { value: "gte", label: "Mayor o igual" },
    ],
    rules: fixatServiceValidationRules.time_periods,
  },
  {
    name: "priority",
    label: "Prioridad",
    placeholder: "Ingresa la prioridad del servicio",
    variant: "input",
    inputType: "number",
    rules: fixatServiceValidationRules.priority,
  },
  {
    name: "minimum_periods",
    label: "Periodos mínimos",
    placeholder: "Ingresa el mínimo de periodos",
    variant: "input",
    inputType: "number",
    rules: fixatServiceValidationRules.minimum_periods,
  },
];

export function htmlListToArray(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const result = [];

  let container = doc.body.firstElementChild;
  if (!container || container.tagName !== "DIV") {
    // Fallback to <body> if there's no <div> root
    container = doc.body;
  }

  function traverseList(listElement, depth = 0, isRoot = true) {
    const listType = listElement.tagName.toLowerCase(); // "ul" or "ol"
    const children = Array.from(listElement.children).filter(
      (el) => el.tagName === "LI"
    );

    for (const li of children) {
      // Get visible text (ignores nested <ul>/<ol>)
      // Only extract direct <p> content
      const pNode = li.querySelector("p");
      const textContent = pNode?.textContent?.trim();
      if (textContent) {
        let prefix = "";

        if (isRoot) {
          prefix = listType === "ul" ? "º" : "";
        } else {
          prefix = "#";
        }

        result.push(`${prefix}${textContent}`);
      }

      // Traverse direct child lists inside this <li>
      const subLists = Array.from(li.children).filter((el) =>
        ["UL", "OL"].includes(el.tagName)
      );

      for (const subList of subLists) {
        traverseList(subList, depth + 1, false);
      }
    }
  }

  // Start only from root-level <ul>/<ol> inside the <div> or body if fallback
  const rootLists = Array.from(container.children).filter(
    (el) => el.tagName === "UL" || el.tagName === "OL"
  );

  for (const list of rootLists) {
    traverseList(list, 0, true);
  }

  return result;
}

export function arrayToHtmlList(items) {
  if (!items) return "";
  const createLi = (text) => `<li><p>${text}</p></li>`;
  const wrapList = (type, children) =>
    `<${type}>${children.join("")}</${type}>`;

  const root = [];
  let currentListType = null;
  let currentListItems = [];
  let lastLiWithPotentialSublist = null;

  const flushList = () => {
    if (currentListItems.length > 0) {
      root.push(wrapList(currentListType, currentListItems));
      currentListItems = [];
      currentListType = null;
      lastLiWithPotentialSublist = null;
    }
  };

  for (let i = 0; i < items.length; i++) {
    const raw = items[i];
    const prefix = raw[0];
    const content = prefix === "º" || prefix === "#" ? raw.slice(1) : raw;

    if (prefix === "#") {
      if (!lastLiWithPotentialSublist) {
        continue; // Skip invalid nested item without parent
      }

      // Extract sublist from last <li> (remove it temporarily)
      const liHtml = lastLiWithPotentialSublist.li;
      const subType = lastLiWithPotentialSublist.type;

      // Create sublist and re-attach it to the <li>
      lastLiWithPotentialSublist.subItems.push(createLi(content));

      const newLi = liHtml.replace(
        "</li>",
        wrapList(subType, lastLiWithPotentialSublist.subItems) + "</li>"
      );

      // Replace last <li> in currentListItems
      currentListItems[currentListItems.length - 1] = newLi;
    } else {
      const type = prefix === "º" ? "ul" : "ol";

      if (currentListType && type !== currentListType) {
        flushList();
      }

      currentListType = type;

      const li = createLi(content);
      currentListItems.push(li);

      // Prepare to allow nesting if next item is "#"
      lastLiWithPotentialSublist = {
        li,
        subItems: [],
        type: type, // nested list mimics parent type
      };
    }
  }

  flushList();

  return `<div>${root.join("")}</div>`;
}
